import { memo, useCallback, useEffect, useState } from "react";

import Clock from "@zenith/assets/icons/Clock";
import CloseCircle from "@zenith/assets/icons/CloseCircle";
import Info from "@zenith/assets/icons/Info";
import {
  ButtonComponent,
  ModalComponent,
  SelectComponent,
  SpinnerComponent,
  TextFieldComponent,
} from "@zenith/components";
import { useUpgradeContext, useUserDetailsContext } from "@zenith/context";
import {
  ONLY_CHARACTERS_NUMBERS_AND_SPACES_REGEX,
  UPGRADE_REG_DETAILS_KEY,
} from "@zenith/data/constants";
import StepStatusComponent from "@zenith/features/Tiers/components/StepStatusComponent/step-status-component/step-status-component";
import {
  setBusinessNameValidations,
  setRegistrationNumberValidations,
  setTaxIdentificationNumberValidations,
} from "@zenith/features/Tiers/validations";
import { AppNavigation, AppStepper, usePolling, TierStepper } from "@zenith/hooks";
import { UPGRADE_ACCOUNT } from "@zenith/routes/paths";
import {
  ApiResponseDTODiscardUpgradeResponseDTO,
  ApiResponseDTORegistrationIdResponseDTO,
  TierApiService,
  TierStepsResponseDTO,
} from "@zenith/services/user";
import { ApiResponseDTORegistrationDetailsResponseDTO as ApiResponseDTORegistrationDetailsDTO } from "@zenith/services/user/models/ApiResponseDTORegistrationDetailsResponseDTO";
import { RegistrationDetailsRequestDTO as RegistrationDetailsDTO } from "@zenith/services/user/models/RegistrationDetailsRequestDTO";
import { UsersApiService } from "@zenith/services/user/services/UsersApiService";
import {
  getApiErrorMessage,
  updateTierStepStatus,
} from "@zenith/utils/Helpers";
import { FormDataFields, FormErrors, RegistrationScreen } from "./interfaces";
import CheckCircle from "@zenith/assets/icons/CheckCircle";
import RegistrationVerificationCard from "./registration-verification-card";

/* eslint-disable-next-line */
export interface RegistrationDetailsPageProps {
  step: TierStepsResponseDTO;
}

interface ExtendedRegistrationDetailsResponseDTO {
  verificationStatus?: string;
  registrationNumberStatusCode?: string;
  legalName?: string;
}

const SOLE_PROPRIETORSHIP_REGISTRATION_TYPE = "SOLE_PROPRIETORSHIP";
const REGISTRATION_TYPE_ITEMS = [
  {
    value: SOLE_PROPRIETORSHIP_REGISTRATION_TYPE,
    label: "Business Name/Sole proprietorship",
  },
];

const NO_ERROR = { message: "", hasError: false };

const INITIAL_FORM_DATA: FormDataFields = {
  registrationNumber: "",
  taxIdentificationNumber: "",
  businessName: "",
};

const INITIAL_FORM_ERRORS: FormErrors = {
  registrationNumber: NO_ERROR,
  taxIdentificationNumber: NO_ERROR,
  businessName: NO_ERROR,
};

const toApiRegistrationNumber = (registrationNumber: string) =>
  registrationNumber.trim().toUpperCase().replace(/[\s-]/g, "");

const RegistrationDetailsPage = memo(function RegistrationDetailsPage(
  props: RegistrationDetailsPageProps
) {
  const {
    step: { stepStatus },
  } = props;

  const { navigateTo } = AppNavigation();
  const { setActiveStep } = AppStepper();
  const { goToNextOpenStep } = TierStepper();
  const { upgradeContext, setUpgradeContext, clearUpgradeContext } =
    useUpgradeContext();
  const { userDetailsContext } = useUserDetailsContext();

  const registrationDetailsInitialStatus = upgradeContext.isRecoveryFlow
    ? stepStatus
    : undefined;
  const [backModal, setBackModal] = useState<boolean>(false);
  const [isContinueDisabled, setIsContinueDisabled] = useState<boolean>(false);
  const [registrationDetailsStartPage, setRegistrationDetailsStartPage] =
    useState<boolean>(!upgradeContext.isRecoveryFlow);
  const [registrationDetailsStatus, setRegistrationDetailsStatus] = useState<
    string | undefined
  >(registrationDetailsInitialStatus);
  const [
    registrationDetailsValidationTrigger,
    setRegistrationDetailsValidationTrigger,
  ] = useState<boolean>(false);
  const [verifyingRegistrationDetails, setVerifyingRegistrationDetails] =
    useState<boolean>(!upgradeContext.isRecoveryFlow);
  const [formDataErrors, setFormDataErrors] =
    useState<FormErrors>(INITIAL_FORM_ERRORS);
  const [formData, setFormData] = useState<FormDataFields>(INITIAL_FORM_DATA);
  const [verificationStatusCode, setVerificationStatusCode] = useState<
    string | null
  >(null);
  const [has1015Error, setHas1015Error] = useState<boolean>(false);

  const [registrationScreen, setRegistrationScreen] =
    useState<RegistrationScreen>("idle");
  const [isVerifyingRegistrationId, setIsVerifyingRegistrationId] =
    useState<boolean>(false);
  const [isTinFromLookup, setIsTinFromLookup] = useState<boolean>(false);
  const [notVerifiedModal, setNotVerifiedModal] = useState<{
    title: string;
    body: string;
  } | null>(null);

  const apiRegistrationNumber = toApiRegistrationNumber(
    formData.registrationNumber
  );
  const registrationPrefix = apiRegistrationNumber.startsWith("BN")
    ? "BN"
    : "RC";
  const isRegistrationNumberValid = !setRegistrationNumberValidations(
    apiRegistrationNumber
  ).hasError;

  const resetVerification = () => {
    setRegistrationScreen("idle");
    setIsTinFromLookup(false);
    setFormData((prev) => ({
      ...prev,
      taxIdentificationNumber: "",
      businessName: "",
    }));
    setFormDataErrors((prev) => ({
      ...prev,
      taxIdentificationNumber: NO_ERROR,
      businessName: NO_ERROR,
    }));
  };

  const onRegistrationNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const registrationNumber = (event.target.value as string)
      .toUpperCase()
      .replace(/[^A-Z0-9-]/g, "");
    setFormDataErrors((prev) => ({ ...prev, registrationNumber: NO_ERROR }));
    setFormData((prev) => ({ ...prev, registrationNumber }));
    if (registrationScreen !== "idle") resetVerification();
  };

  const onTaxIdentificationNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    let taxIdentificationNumber = event.target.value as string;
    taxIdentificationNumber = taxIdentificationNumber.replace(/[^\d]/g, "");
    if (taxIdentificationNumber.length > 1) {
      taxIdentificationNumber = taxIdentificationNumber.replace(
        /(\d{8})(\d)/,
        "$1-$2"
      );
    }
    setFormDataErrors((prev) => ({
      ...prev,
      taxIdentificationNumber: NO_ERROR,
    }));
    setFormData((prev) => ({ ...prev, taxIdentificationNumber }));
  };

  const onBusinessNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const businessName = (event.target.value as string).replace(
      ONLY_CHARACTERS_NUMBERS_AND_SPACES_REGEX,
      ""
    );
    setFormDataErrors((prev) => ({ ...prev, businessName: NO_ERROR }));
    setFormData((prev) => ({ ...prev, businessName }));
  };

  const onFieldBlur = (
    field: keyof FormDataFields,
    validator: (field: string) => { message: string; hasError: boolean }
  ) => {
    const value =
      field === "registrationNumber" ? apiRegistrationNumber : formData[field];
    setFormDataErrors((prev) => ({ ...prev, [field]: validator(value) }));
  };

  const isTinValid = formData.taxIdentificationNumber.trim() !== "";
  const isBusinessNameValid = !setBusinessNameValidations(
    formData.businessName.trim()
  ).hasError;
  const isProceedDisabled =
    isContinueDisabled || !isTinValid || !isBusinessNameValid;

  const handleContinueButton = () => {
    goToNextOpenStep();
  };

  const handleModalState = () => {
    setBackModal(!backModal);
  };

  const handleDiscardUpgrade = () => {
    TierApiService.discardUpgradeTier()
      .then((response: ApiResponseDTODiscardUpgradeResponseDTO) => {
        if ("data" in response && response.data) {
          const storageKey = `${UPGRADE_REG_DETAILS_KEY}_${userDetailsContext.phoneNumber}`;
          localStorage.removeItem(storageKey);
          setActiveStep(0);
          clearUpgradeContext();
          navigateTo(UPGRADE_ACCOUNT);
        }
      })
      .catch((error) => {
        return error;
      });
  };

  const handleTryAgain = () => {
    setFormData(INITIAL_FORM_DATA);
    setFormDataErrors(INITIAL_FORM_ERRORS);
    setRegistrationScreen("idle");
    setIsTinFromLookup(false);
    setRegistrationDetailsValidationTrigger(false);
    setVerifyingRegistrationDetails(true);
    setRegistrationDetailsStatus(undefined);
    setRegistrationDetailsStartPage(true);
    setVerificationStatusCode(null);
    setHas1015Error(false);
  };

  const showRegistrationDetailsError = (message: string) =>
    setNotVerifiedModal({
      title: "Could Not Save Registration Details",
      body: message,
    });

  const showRegistrationIdNotVerified = (message?: string) =>
    setNotVerifiedModal({
      title: "Could Not Verify Registration ID",
      body:
        message ||
        "We could not verify the registration ID given, kindly check through and try again",
    });

  const showTinNotVerified = (message?: string) =>
    setNotVerifiedModal({
      title: "Could Not Verify TIN",
      body:
        message ||
        "We could not verify the TIN given, kindly check through and try again",
    });

  const isServiceUnavailable = (error: any) => error?.status === 503;

  const enterDetailsManually = () => {
    setIsTinFromLookup(false);
    setFormDataErrors((prev) => ({
      ...prev,
      taxIdentificationNumber: NO_ERROR,
      businessName: NO_ERROR,
    }));
    setRegistrationScreen("manual");
  };

  const completeRegistrationDetailsStep = (
    stepStatus = TierStepsResponseDTO.stepStatus.APPROVED
  ) => {
    const tierStepsUpdated: TierStepsResponseDTO[] = updateTierStepStatus(
      upgradeContext,
      TierStepsResponseDTO.stepName.REGISTRATION_DETAILS_STEP,
      stepStatus
    );

    setUpgradeContext({
      ...upgradeContext,
      businessLegalName: formData.businessName.trim(),
      tierSteps: tierStepsUpdated,
    });

    goToNextOpenStep();
  };

  const handleVerifyRegistrationId = async () => {
    if (!isRegistrationNumberValid) {
      onFieldBlur("registrationNumber", setRegistrationNumberValidations);
      return;
    }

    setIsVerifyingRegistrationId(true);

    try {
      const response: ApiResponseDTORegistrationIdResponseDTO =
        await UsersApiService.verifyRegistrationId({
          registrationNumber: apiRegistrationNumber,
          registrationType: SOLE_PROPRIETORSHIP_REGISTRATION_TYPE,
        });
      const businessName = response.data?.businessName;

      if (!response.success || !businessName) {
        showRegistrationIdNotVerified(response.message);
        return;
      }

      const taxIdentificationNumber =
        response.data?.taxIdentificationNumber ?? "";
      setFormData((prev) => ({
        ...prev,
        businessName,
        taxIdentificationNumber,
      }));
      setIsTinFromLookup(Boolean(taxIdentificationNumber));
      setRegistrationScreen("verified");
    } catch (error: any) {
      if (isServiceUnavailable(error)) {
        enterDetailsManually();
      } else {
        showRegistrationIdNotVerified(getApiErrorMessage(error, ""));
      }
    } finally {
      setIsVerifyingRegistrationId(false);
    }
  };

  // YouVerify is unavailable: the typed details go to the Back Office for review without another
  // verification, and the merchant moves on to business details while the step is pending
  const submitRegistrationDetailsForReview = () => {
    const request: RegistrationDetailsDTO = {
      registrationNumber: apiRegistrationNumber,
      taxIdentificationNumber: formData.taxIdentificationNumber,
      legalName: formData.businessName.trim(),
    };

    UsersApiService.submitRegistrationDetailsForReview(request)
      .then((response: ApiResponseDTORegistrationDetailsDTO) => {
        setIsContinueDisabled(false);

        if (response.success === false) {
          showRegistrationDetailsError(getApiErrorMessage({ body: response }));
          return;
        }

        completeRegistrationDetailsStep(
          TierStepsResponseDTO.stepStatus.PENDING
        );
      })
      .catch((error) => {
        setIsContinueDisabled(false);
        showRegistrationDetailsError(getApiErrorMessage(error));
      });
  };

  const verifyTypedTin = () => {
    UsersApiService.verifyTin({
      taxIdentificationNumber: formData.taxIdentificationNumber,
    })
      .then((response) => {
        setIsContinueDisabled(false);

        if (response.success) {
          completeRegistrationDetailsStep();
        } else {
          showTinNotVerified(response.message);
        }
      })
      .catch((error) => {
        setIsContinueDisabled(false);

        if (isServiceUnavailable(error)) {
          enterDetailsManually();
        } else {
          showTinNotVerified(getApiErrorMessage(error, ""));
        }
      });
  };

  const handleProceed = (): void => {
    if (registrationScreen === "idle" || isProceedDisabled) return;

    if (registrationScreen === "manual") {
      setIsContinueDisabled(true);
      submitRegistrationDetailsForReview();
      return;
    }

    if (isTinFromLookup) {
      completeRegistrationDetailsStep();
      return;
    }

    setIsContinueDisabled(true);
    verifyTypedTin();
  };

  const fetchData = useCallback(
    () => UsersApiService.getRegistrationDetailsVerificationStatus(),
    []
  );

  const {
    response,
    polling,
    error: pollingError,
  } = usePolling(
    registrationDetailsValidationTrigger && !has1015Error, // Don't poll if we have 1015 error
    !verifyingRegistrationDetails,
    fetchData
  );

  useEffect(() => {
    if (response) {
      setVerifyingRegistrationDetails(polling);

      const responseData = (response as ApiResponseDTORegistrationDetailsDTO)
        .data;
      const status = responseData?.verificationStatus || "";

      const extendedData =
        responseData as ExtendedRegistrationDetailsResponseDTO;
      const statusCode = extendedData?.registrationNumberStatusCode || null;
      setVerificationStatusCode(statusCode);

      if (status !== "PENDING") {
        setVerifyingRegistrationDetails(false);
      }

      setRegistrationDetailsStatus(status);

      const finalStatus =
        status?.mapUpgradeStatus() as TierStepsResponseDTO.stepStatus;

      const tierStepsUpdated: TierStepsResponseDTO[] = updateTierStepStatus(
        upgradeContext,
        TierStepsResponseDTO.stepName.REGISTRATION_DETAILS_STEP,
        finalStatus
      );

      if (
        status === "SUCCESS" ||
        status === TierStepsResponseDTO.stepStatus.APPROVED
      ) {
        const legalName = extendedData?.legalName || "";
        setUpgradeContext({
          ...upgradeContext,
          businessLegalName: legalName,
          tierSteps: tierStepsUpdated,
        });
      } else {
        setUpgradeContext({
          ...upgradeContext,
          businessLegalName: "",
          tierSteps: tierStepsUpdated,
        });
      }
    }
  }, [response, polling]);

  useEffect(() => {
    if (pollingError) {
      setVerifyingRegistrationDetails(false);
      setRegistrationDetailsStatus(TierStepsResponseDTO.stepStatus.REJECTED);
    }
  }, [pollingError]);

  const registrationNote = (
    <div className="flex items-start gap-x-3 rounded-lg bg-violet-light px-4 py-3 text-xs text-neutral-900">
      <Info className="mt-0.5 shrink-0" />
      <p>
        <span className="font-semibold">Note:</span> Only Business Name and Sole
        Proprietorship registrations are accepted. Other registration type are
        not eligible for Tier 4. Using one will make your Tier 4 upgrade
        invalid.
      </p>
    </div>
  );

  const taxIdentificationNumberField = (
    <div className="flex flex-col">
      <TextFieldComponent
        id="tax-identification-number-field"
        labelId="tax-identification-number-field-label"
        containerClassName="flex grow"
        hasError={formDataErrors.taxIdentificationNumber.hasError}
        label="TIN"
        placeholder="Enter TIN Number"
        type="text"
        value={formData.taxIdentificationNumber}
        onChange={onTaxIdentificationNumberChange}
        onBlur={() =>
          onFieldBlur(
            "taxIdentificationNumber",
            setTaxIdentificationNumberValidations
          )
        }
        maxLength={13}
      />
      {formDataErrors.taxIdentificationNumber.hasError && (
        <div className="ml-2 mt-1 text-xs text-red-400">
          <span className="break-words">
            {formDataErrors.taxIdentificationNumber.message}
          </span>
        </div>
      )}
    </div>
  );

  return (
    <>
      <ModalComponent
        body="Are you sure you want to leave?"
        secondLine="All information provided will be lost."
        title="Discard Upgrade"
        showModal={backModal}
        onClickClose={handleModalState}
        doubleOptions={true}
        onLeftButtonClick={handleDiscardUpgrade}
        onRightButtonClick={handleModalState}
        leftButtonText="Discard Upgrade"
        rightButtonText="Return to Upgrade"
      />
      <ModalComponent
        title={notVerifiedModal?.title ?? ""}
        body={notVerifiedModal?.body ?? ""}
        showModal={Boolean(notVerifiedModal)}
        onClickClose={() => setNotVerifiedModal(null)}
        icon="hexagonalWarning"
        showCloseIcon
        hideButtons
      />

      {registrationDetailsStartPage ? (
        <form onSubmit={(event) => event.preventDefault()}>
          <div className="mt-5">
            <div className="mb-12 flex flex-col gap-4">
              <h1 className="text-4.5xl font-medium text-neutral-900">
                Business Registration Details
              </h1>
              <h3 className="text-base font-normal text-neutral-600">
                Please provide your official business registration information
                to proceed with Tier 4 verification.
              </h3>
            </div>

            <div className="flex flex-col gap-4">
              <SelectComponent
                items={REGISTRATION_TYPE_ITEMS}
                className="w-auto"
                label="Registration Type"
                id="registration-type-field"
                labelId="registration-type-field-label"
                value={SOLE_PROPRIETORSHIP_REGISTRATION_TYPE}
                placeholder="Select registration type"
                onChange={() => undefined}
                editable={false}
                disabled={true}
              />

              <div className="flex flex-col">
                <TextFieldComponent
                  id="registration-number-field"
                  labelId="registration-number-field-label"
                  containerClassName="flex grow"
                  hasError={formDataErrors.registrationNumber.hasError}
                  label="Registration ID"
                  placeholder="Enter RC or BN Number"
                  type="text"
                  value={formData.registrationNumber}
                  onChange={onRegistrationNumberChange}
                  onBlur={() =>
                    onFieldBlur(
                      "registrationNumber",
                      setRegistrationNumberValidations
                    )
                  }
                  maxLength={20}
                />
                {formDataErrors.registrationNumber.hasError && (
                  <div className="ml-2 mt-1 text-xs text-red-400">
                    <span className="break-words">
                      {formDataErrors.registrationNumber.message}
                    </span>
                  </div>
                )}
              </div>

              {registrationScreen === "verified" && (
                <>
                  <RegistrationVerificationCard
                    registrationPrefix={registrationPrefix}
                    businessName={formData.businessName}
                    taxIdentificationNumber={
                      isTinFromLookup
                        ? formData.taxIdentificationNumber
                        : undefined
                    }
                  />
                  {!isTinFromLookup && taxIdentificationNumberField}
                </>
              )}

              {registrationScreen === "manual" && (
                <>
                  <div className="flex flex-col">
                    <TextFieldComponent
                      id="business-name-field"
                      labelId="business-name-field-label"
                      containerClassName="flex grow"
                      hasError={formDataErrors.businessName.hasError}
                      label="Business Name"
                      placeholder="Enter your Business Name"
                      type="text"
                      value={formData.businessName}
                      onChange={onBusinessNameChange}
                      onBlur={() =>
                        onFieldBlur("businessName", setBusinessNameValidations)
                      }
                      maxLength={50}
                    />
                    {formDataErrors.businessName.hasError && (
                      <div className="ml-2 mt-1 text-xs text-red-400">
                        <span className="break-words">
                          {formDataErrors.businessName.message}
                        </span>
                      </div>
                    )}
                  </div>
                  {taxIdentificationNumberField}
                </>
              )}

              {(registrationScreen === "idle" ||
                (registrationScreen === "verified" && isTinFromLookup)) &&
                registrationNote}
            </div>

            <div className="stepper-buttons-container lg:justify-end">
              {registrationScreen === "idle" ? (
                <ButtonComponent
                  label="Verify"
                  onClick={() => handleVerifyRegistrationId()}
                  disabled={
                    !isRegistrationNumberValid || isVerifyingRegistrationId
                  }
                  isLoading={isVerifyingRegistrationId}
                  size="large"
                  type="primary"
                  id="verify-registration-id-button"
                />
              ) : (
                <>
                  <ButtonComponent
                    label="Back"
                    onClick={resetVerification}
                    disabled={isContinueDisabled}
                    size="large"
                    type="outline"
                    id="back-button"
                  />

                  <ButtonComponent
                    label="Proceed"
                    onClick={handleProceed}
                    disabled={isProceedDisabled}
                    isLoading={isContinueDisabled}
                    size="large"
                    type="primary"
                    id="continue-button"
                  />
                </>
              )}
            </div>
          </div>
        </form>
      ) : (
        <div className="mt-6 flex flex-col">
          <h1 className="mb-16 text-4.5xl font-medium text-neutral-900">
            Registration Details
          </h1>

          {!verifyingRegistrationDetails &&
            (registrationDetailsStatus === "PENDING" ||
              registrationDetailsStatus ===
                TierStepsResponseDTO.stepStatus.PENDING) && (
              <StepStatusComponent
                status={TierStepsResponseDTO.stepStatus.PENDING}
                title={"Waiting For Approval"}
                subTitle={'Press "Continue" to proceed.'}
                icon={<Clock transform="scale(1.70)" />}
                handleDiscardButton={handleModalState}
                handleContinueButton={handleContinueButton}
              />
            )}

          {!verifyingRegistrationDetails &&
            (registrationDetailsStatus === "SUCCESS" ||
              registrationDetailsStatus ===
                TierStepsResponseDTO.stepStatus.APPROVED) && (
              <StepStatusComponent
                status={TierStepsResponseDTO.stepStatus.APPROVED}
                title={"Information Successfully Verified"}
                subTitle={'Press "Continue" to proceed.'}
                icon={<CheckCircle transform="scale(1.70)" />}
                handleDiscardButton={handleModalState}
                handleContinueButton={handleContinueButton}
              />
            )}

          {!verifyingRegistrationDetails &&
            (registrationDetailsStatus === "FAILED" ||
              registrationDetailsStatus ===
                TierStepsResponseDTO.stepStatus.REJECTED) && (
              <StepStatusComponent
                status={TierStepsResponseDTO.stepStatus.REJECTED}
                title={"Information Verification Failed"}
                subTitle={"Please, try this again."}
                icon={<CloseCircle transform="scale(1.70)" />}
                handleDiscardButton={handleModalState}
                handleTryAgainButton={handleTryAgain}
              />
            )}

          {verifyingRegistrationDetails && (
            <div className="flex flex-col items-center">
              <SpinnerComponent size={64} type={"primary"} showIcon={false} />
              <p className="mt-6 text-base font-normal text-neutral-600">
                Your information is being verified.
              </p>
              <p className="text-sm font-normal text-neutral-400">
                This should not take too long.
              </p>
            </div>
          )}
        </div>
      )}
    </>
  );
});

export default RegistrationDetailsPage;
